import * as Sentry from "@sentry/react";

function resolveSentryEnvironment(): string {
  const configuredEnvironment = import.meta.env.VITE_SENTRY_ENVIRONMENT?.trim();
  if (configuredEnvironment) return configuredEnvironment;
  return import.meta.env.PROD ? "production" : import.meta.env.MODE;
}

function resolveSentryRelease(): string | undefined {
  const configuredRelease = import.meta.env.VITE_SENTRY_RELEASE?.trim();
  if (configuredRelease) return configuredRelease;

  const buildRelease = __APP_RELEASE__.trim();
  return buildRelease || undefined;
}

export function initSentry() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  const environment = resolveSentryEnvironment();
  const release = resolveSentryRelease();

  // Only initialize if DSN is provided
  if (!dsn) {
    console.warn("Sentry DSN not configured. Error tracking disabled.");
    return;
  }

  Sentry.init({
    dsn,
    environment,
    release,

    // Performance Monitoring
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // Reduce in production for cost efficiency
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,

    // Capture Replay for 10% of all sessions,
    // plus for 100% of sessions with an error
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      /^chrome-extension:\/\//,
      /^moz-extension:\/\//,
      // Network errors that are expected
      "Network request failed",
      "Failed to fetch",
      "Load failed",
      // User-initiated navigation
      "AbortError",
    ],

    // Add user context when available
    beforeSend(event) {
      // Drop known-noise events which should not create Sentry Issues.
      // Breadcrumbs + metrics cover these cases without polluting Issues.
      const message = event.message || event.logentry?.message;
      if (message) {
        if (
          message === "Connection restored" ||
          message === "Connection lost"
        ) {
          return null;
        }
        if (message.startsWith("Poor Web Vital:")) {
          return null;
        }
      }

      // Don't send events in development unless explicitly enabled
      if (import.meta.env.DEV && !import.meta.env.VITE_SENTRY_DEV_ENABLED) {
        console.log("[Sentry] Event captured (dev mode, not sent):", event);
        return null;
      }
      return event;
    },
  });
}

export function setSentryUser(user: {
  id: string;
  email?: string;
  organizationId?: string;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    organizationId: user.organizationId,
  } as Sentry.User & { organizationId?: string });
}

export function clearSentryUser() {
  Sentry.setUser(null);
}

export function captureError(error: Error, context?: Record<string, unknown>) {
  Sentry.captureException(error, {
    extra: context,
  });
}

export function captureMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, unknown>,
) {
  Sentry.captureMessage(message, {
    level,
    extra: context,
  });
}

export { Sentry };
